import React from 'react';
import styled from 'styled-components';

import { useCurrentTheme } from 'app/shared/theme';
import {
  VideosPageCity as City,
  VideosPageSong as Song,
  VideosPageVideo as Video,
} from 'app/shared/typings';
import {
  Box,
  Image,
  NavigationLink,
  Typography,
} from 'app/shared/components/atoms';

export interface VideoThumbnailProps {
  video: Video;
}

// TODO: Update this to a new Thumbnail from Product/Design
const defaultThumbnailImgUrl =
  'https://img.youtube.com/vi/XRgWVsXq9wk/maxresdefault.jpg';

const VideoThumbnailLink = styled(NavigationLink)`
  :hover {
    picture {
      transition: all 0.1s ease-in-out;
      transform: scale(1.05);
    }
  }
`;

function formatForDefaultUrl(thumbnailImageUrl?: string): string {
  if (!thumbnailImageUrl || thumbnailImageUrl == '') {
    return defaultThumbnailImgUrl;
  }
  return thumbnailImageUrl.replace('maxresdefault.jpg', 'default.jpg'); // Use the default image which is more reliable and smaller
}

function formatTimeAndPlace({
  video,
  city,
  song,
}: {
  video?: Video;
  city?: City;
  song?: Song;
}) {
  const performedAt = video?.performedAt || null;
  const cityTitle = city?.title || null;
  const songCityTitle = song?.city?.title || null;

  const performedAtIsValid = !!(performedAt && Date.parse(performedAt));
  const time = performedAtIsValid
    ? new Date(performedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const place = cityTitle ? cityTitle : songCityTitle;

  if (time) {
    return time + (place ? ` | Sofar ${place}` : '');
  }

  if (place) {
    return `Sofar ${place}`;
  }

  return '';
}

export const VideoThumbnail: React.FC<
  React.PropsWithChildren<VideoThumbnailProps>
> = ({ video }) => {
  const { song, city, cachedSlug, displayName, artist, thumbnailImageUrl } =
    video;
  const { borderRadius } = useCurrentTheme();

  return (
    <VideoThumbnailLink
      href={`/videos/${cachedSlug}`}
      data-qaid="video-thumbnail"
      textDecoration="none"
      w="100%"
    >
      <Image
        src={thumbnailImageUrl || formatForDefaultUrl(thumbnailImageUrl)}
        alt={displayName}
        w="100%"
        borderRadius={borderRadius.card}
        overflow="hidden"
        m={0}
        pt={8}
      />
      <Box mt={2} flexDirection="column">
        <Typography
          as="h3"
          textOverflow="ellipsis"
          overflow="hidden"
          textWrap="nowrap"
          fontSize="fontSize400"
          fontFamily="title"
        >
          {displayName}
        </Typography>
        {artist?.title && <Typography>{artist.title}</Typography>}
        <Typography fontSize="fontSize50" mt={1}>
          {formatTimeAndPlace({ video, city, song })}
        </Typography>
      </Box>
    </VideoThumbnailLink>
  );
};
